<template>
	<app-content class="h-panel cash-flow-config">
		<div class="h-panel-bar">
			<span class="h-panel-title">现金流量表配置</span>
			<div class="actions">
				<Button class="action-btn" size="small" @click="applyStandardTemplate(false)" :loading="standardLoading">应用标准模板</Button>
				<Button class="action-btn warn" size="small" @click="applyStandardTemplate(true)" :loading="standardLoading">覆盖标准模板</Button>
				<Button class="action-btn success" size="small" @click="resetToStandard" :loading="resetLoading">重置为标准配置</Button>
				<Button class="action-btn primary" size="small" color="primary" @click="applyConfig" :loading="applyLoading">保存科目映射配置</Button>
			</div>
		</div>

		<div class="padding summary-area">
			<Row type="flex" :space="10" class="summary-row">
				<Cell v-for="item in summaryCards" :key="item.key">
					<div class="summary-card">
						<div class="summary-title">{{item.title}}</div>
						<div class="summary-value">{{item.value}}</div>
						<div class="summary-desc">{{item.desc}}</div>
					</div>
				</Cell>
			</Row>
		</div>

		<Tabs :datas="tabDatas" v-model="selectedTab"/>

		<div class="tab-section padding">
			<div v-if="selectedTab === 'cash_equivalents'">
				<div class="toolbar">
					<Input v-model="cashKeyword" placeholder="按科目编码/名称筛选" clearable/>
				</div>
				<Table :datas="filteredCashEquivalents" border :stripe="true">
					<TableItem title="科目编码" prop="subjectCode" :width="120"/>
					<TableItem title="科目名称" prop="subjectName"/>
					<TableItem title="类型" :width="120">
						<template slot-scope="{data}">
							<Tag :color="data.isCash ? 'green' : 'blue'">{{formatCashType(data)}}</Tag>
						</template>
					</TableItem>
					<TableItem title="到期天数" prop="maturityDays" :width="100">
						<template slot-scope="{data}">
							{{data.maturityDays != null ? data.maturityDays : '-'}}
						</template>
					</TableItem>
					<TableItem title="说明">
						<template slot-scope="{data}">
							{{data.description || '-'}}
						</template>
					</TableItem>
				</Table>
			</div>

			<div v-else-if="selectedTab === 'flow_categories'">
				<div class="toolbar">
					<span class="dark4-color">符合《企业会计准则》33 个标准项目，可直接同步到报表模板。</span>
				</div>
				<Table :datas="sortedFlowCategories" border :stripe="true">
					<TableItem title="项目编码" prop="categoryCode" :width="150"/>
					<TableItem title="项目名称">
						<template slot-scope="{data}">
							<span :style="{paddingLeft: ((data.level - 1) * 16) + 'px', fontWeight: data.isBold ? 600 : 400}">
								{{data.categoryName}}
							</span>
						</template>
					</TableItem>
					<TableItem title="活动类型" :width="120">
						<template slot-scope="{data}">
							<Tag :color="activityColor(data.activityType)">{{formatActivity(data.activityType)}}</Tag>
						</template>
					</TableItem>
					<TableItem title="现金方向" :width="100">
						<template slot-scope="{data}">
							<Tag :color="directionColor(data.cashDirection)">{{formatDirection(data.cashDirection)}}</Tag>
						</template>
					</TableItem>
					<TableItem title="行次" prop="lineNumber" :width="80"/>
				</Table>
			</div>

			<div v-else-if="selectedTab === 'subject_mapping'">
				<div class="toolbar">
					<Input v-model="mappingKeyword" placeholder="科目/项目关键词" clearable/>
					<Select v-model="activityFilter" placeholder="按活动类型" :datas="activityOptions" :clearable="true" style="width: 140px"/>
					<Select v-model="directionFilter" placeholder="按现金方向" :datas="directionOptions" :clearable="true" style="width: 140px"/>
					<Select v-model="categoryFilter" placeholder="按现金流量项目" :datas="categoryOptions" :clearable="true" filterable style="min-width: 220px"/>
					<Input v-model="unmappedMonth" placeholder="月份(yyyy-MM)" style="width: 140px"/>
					<Button size="small" @click="fetchValidation" :loading="validateLoading">检查缺口</Button>
					<Button size="small" color="primary" @click="autoMapByStandard" :loading="autoMapLoading">自动补全映射</Button>
					<Button size="small" color="primary" @click="openAddMapping">新增映射</Button>
				</div>
				<div class="unmapped-panel" v-if="validation.unmappedWithActivity.length">
					<div class="panel-title">当月未映射科目（有发生额）</div>
					<Table :datas="validation.unmappedWithActivity" border :stripe="true">
						<TableItem title="科目编码" prop="subjectCode" :width="120"/>
						<TableItem title="科目名称" prop="subjectName"/>
						<TableItem title="科目类型" prop="subjectType" :width="100"/>
						<TableItem title="余额方向" prop="balanceDirection" :width="100"/>
						<TableItem title="借方发生额" prop="debitAmount" :width="140"/>
						<TableItem title="贷方发生额" prop="creditAmount" :width="140"/>
					</Table>
				</div>
				<Table :datas="filteredMappings" border :stripe="true">
					<TableItem title="科目编码" prop="subjectCode" :width="110"/>
					<TableItem title="科目名称" prop="subjectName"/>
					<TableItem title="现金流量项目" :width="260">
						<template slot-scope="{data}">
							<div class="mapping-title">{{(flowCategoryMap[data.cashFlowCategoryCode] && flowCategoryMap[data.cashFlowCategoryCode].categoryName) || '-'}}</div>
							<div class="mapping-meta">{{data.cashFlowCategoryCode}}</div>
						</template>
					</TableItem>
					<TableItem title="借方流向" :width="140">
						<template slot-scope="{data}">
							<Tag :color="directionColor(data.debitDirection)">{{formatDirection(data.debitDirection)}}</Tag>
						</template>
					</TableItem>
					<TableItem title="贷方流向" :width="140">
						<template slot-scope="{data}">
							<Tag :color="directionColor(data.creditDirection)">{{formatDirection(data.creditDirection)}}</Tag>
						</template>
					</TableItem>
					<TableItem title="操作" :width="150" align="center">
						<template slot-scope="{data}">
							<Button size="mini" @click="openEditMapping(data)">编辑</Button>
							<Button size="mini" type="error" @click="removeMapping(data)">删除</Button>
						</template>
					</TableItem>
				</Table>
			</div>

			<div v-else-if="selectedTab === 'formula_config'">
				<div class="toolbar">
					<Input v-model="formulaKeyword" placeholder="按项目名称/ID/取数来源筛选" clearable/>
						<span class="dark4-color">公式取数规则：0净发生额，1借方，2贷方，6期初余额，7/8/9现金对方口径</span>
				</div>
				<Table :datas="filteredFormulas" border :stripe="true">
					<TableItem title="项目ID" prop="templateItemsId" :width="100"/>
					<TableItem title="项目名称">
						<template slot-scope="{data}">
							<div class="mapping-title">{{data.title || (flowCategoryMap[data.fromTag] && flowCategoryMap[data.fromTag].categoryName) || '现金流量项目'}}</div>
						</template>
					</TableItem>
					<TableItem title="计算" :width="80">
						<template slot-scope="{data}">
							<Tag :color="data.calculation === '+' ? 'green' : 'red'">{{data.calculation}}</Tag>
						</template>
					</TableItem>
					<TableItem title="取数规则" :width="130">
						<template slot-scope="{data}">
							{{formatAccessRule(data.accessRules)}}
						</template>
					</TableItem>
					<TableItem title="来源标签" prop="fromTag" :width="120"/>
				</Table>
			</div>
		</div>

		<Modal v-model="mappingModalVisible" hasCloseIcon>
			<div slot="header">{{mappingEditMode ? '编辑科目映射' : '新增科目映射'}} <span style="font-size: 12px; color: #888; font-weight: normal; margin-left: 10px;">提示：此处通常映射对方科目。对方借方(如费用)对应流出，对方贷方(如收入)对应流入</span></div>
			<Form ref="mappingFormRef" :model="mappingForm" :labelWidth="110" :rules="mappingRules">
				<FormItem label="科目编码" prop="subjectCode">
					<div class="subject-input-wrapper">
						<input
							class="manual-input"
							type="text"
							v-model="mappingForm.subjectCode"
							placeholder="如 1001"
							@input="handleSubjectInput($event.target.value)"
							@focus="handleSubjectInput(mappingForm.subjectCode)"
						/>
						<div class="subject-suggest" v-if="subjectDropdownVisible">
							<div class="item" v-for="opt in subjectOptions" :key="opt.code" @mousedown.prevent="selectSubject(opt)" @click.stop>
								<span class="code">{{opt.code}}</span>
								<span class="name">{{opt.name}}</span>
							</div>
							<div class="item" v-if="subjectLoading"><span>加载中...</span></div>
							<div class="item" v-if="!subjectLoading && subjectOptions.length===0"><span>无匹配科目</span></div>
						</div>
					</div>
				</FormItem>
				<FormItem label="科目名称" prop="subjectName">
					<input class="manual-input" v-model="mappingForm.subjectName" placeholder="自动带出科目名称" readonly/>
				</FormItem>
				<FormItem label="现金流量项目" prop="cashFlowCategoryCode">
					<Select v-model="mappingForm.cashFlowCategoryCode" :datas="categoryOptions" filterable clearable style="width: 100%"/>
				</FormItem>
				<FormItem label="借方流向" prop="debitDirection">
					<Select v-model="mappingForm.debitDirection" :datas="directionOptions" clearable/>
				</FormItem>
				<FormItem label="贷方流向" prop="creditDirection">
					<Select v-model="mappingForm.creditDirection" :datas="directionOptions" clearable/>
				</FormItem>
				<FormItem label="启用">
					<Switch v-model="mappingForm.isActive"/>
				</FormItem>
			</Form>
			<div class="text-center margin-top">
				<Button color="green" @click="saveMapping" :loading="savingMapping">保存</Button>
				<Button @click="mappingModalVisible=false">取消</Button>
			</div>
		</Modal>
	</app-content>
</template>

<script>
	import {mapState} from 'vuex';
	import cashFlowConfig from '@/assets/cash-flow-config.json';

	export default {
		name: 'CashFlowConfig',
		data() {
			return {
				selectedTab: 'cash_equivalents',
				cashKeyword: '',
				mappingKeyword: '',
				formulaKeyword: '',
				activityFilter: '',
				directionFilter: '',
				categoryFilter: '',
				applyLoading: false,
				resetLoading: false,
				standardLoading: false,
				validateLoading: false,
				autoMapLoading: false,
				config: {},
				validation: {
					unmappedTotal: 0,
					mappingConflicts: [],
					unmappedWithActivity: []
				},
				unmappedMonth: '',
				tabDatas: [
					{key: 'cash_equivalents', title: '现金及等价物'},
					{key: 'flow_categories', title: '现金流量项目'},
					{key: 'subject_mapping', title: '科目映射规则'},
					{key: 'formula_config', title: '报表公式'}
				],
				mappingModalVisible: false,
				mappingEditMode: false,
				mappingForm: {
					subjectCode: '',
					subjectName: '',
					cashFlowCategoryCode: '',
					debitDirection: '',
					creditDirection: '',
					isActive: true,
					_id: null
				},
				mappingRules: {
					required: ['subjectCode', 'cashFlowCategoryCode']
				},
				savingMapping: false,
				subjectOptions: [],
				subjectDropdownVisible: false,
				subjectLoading: false,
				subjectSearchTimer: null
			};
		},
		computed: {
			...mapState(['currentAccountSets']),
			selectedPresetKey() {
				return this.currentAccountSets && this.currentAccountSets.accountingStandards === 0 ? 'small' : 'enterprise';
			},
			flowCategoryMap() {
				const map = {};
				(this.config.flowCategories || []).forEach(item => {
					map[item.categoryCode] = item;
				});
				return map;
			},
			activityOptions() {
				return [
					{key: 'OPERATING', title: '经营活动'},
					{key: 'INVESTING', title: '投资活动'},
					{key: 'FINANCING', title: '筹资活动'}
				];
			},
			directionOptions() {
				return [
					{key: 'INFLOW', title: '流入'},
					{key: 'OUTFLOW', title: '流出'},
					{key: 'NET', title: '净额'},
					{key: 'NONE', title: '不计入现金流'}
				];
			},
			categoryOptions() {
				return (this.config.flowCategories || []).map(item => ({
					key: item.categoryCode,
					title: `${item.lineNumber}. ${item.categoryName}`
				}));
			},
				summaryCards() {
					return [
					{
						key: 'equivalents',
						title: '现金及等价物',
						value: `${this.config.cashEquivalents.length} 条`,
						desc: '现金、银行存款及90天内到期等价物'
					},
					{
						key: 'categories',
						title: '现金流量项目',
						value: `${this.config.flowCategories.length} 项`,
						desc: '经营/投资/筹资+汇兑影响'
					},
					{
						key: 'mapping',
						title: '科目映射',
						value: `${this.config.subjectMappings.length} 规则`,
						desc: '借贷方向对应现金流入/流出'
					},
						{
							key: 'formula',
							title: '报表公式',
							value: `${this.config.formulas.length} 条`,
							desc: '直接法、净额与期初期末余额'
						},
						{
							key: 'unmapped',
							title: '当月未映射',
							value: `${this.validation.unmappedWithActivity.length} 条`,
							desc: '当期有发生额但未映射'
						}
					];
				},
			filteredCashEquivalents() {
				const keyword = (this.cashKeyword || '').toLowerCase();
				return (this.config.cashEquivalents || []).filter(item => {
					if (!keyword) return true;
					return item.subjectCode.toLowerCase().includes(keyword) || (item.subjectName || '').toLowerCase().includes(keyword);
				});
			},
			sortedFlowCategories() {
				return (this.config.flowCategories || []).slice().sort((a, b) => {
					if (a.sortOrder === b.sortOrder) {
						return a.lineNumber - b.lineNumber;
					}
					return a.sortOrder - b.sortOrder;
				});
			},
			filteredMappings() {
				const kw = (this.mappingKeyword || '').toLowerCase();
				return (this.config.subjectMappings || []).filter(item => {
					const category = this.flowCategoryMap[item.cashFlowCategoryCode];
					const matchKw = !kw || item.subjectCode.toLowerCase().includes(kw) || (item.subjectName || '').toLowerCase().includes(kw) || ((category && category.categoryName) || '').toLowerCase().includes(kw);
					const matchActivity = !this.activityFilter || (category && category.activityType === this.activityFilter);
					const matchCategory = !this.categoryFilter || item.cashFlowCategoryCode === this.categoryFilter;
					const matchDirection = !this.directionFilter || item.debitDirection === this.directionFilter || item.creditDirection === this.directionFilter;
					return matchKw && matchActivity && matchCategory && matchDirection;
				});
			},
			filteredFormulas() {
				const kw = (this.formulaKeyword || '').toLowerCase();
				return (this.config.formulas || []).filter(item => {
					const title = item.title || '';
					const matchKw = !kw || String(item.templateItemsId).includes(kw) || (title.toLowerCase().includes(kw)) || String(item.fromTag || '').toLowerCase().includes(kw);
					return matchKw;
				}).sort((a, b) => {
					if (a.templateItemsId === b.templateItemsId) {
						return a.fromTag > b.fromTag ? 1 : -1;
					}
					return a.templateItemsId - b.templateItemsId;
				});
			}
		},
		methods: {
			formatCashType(item) {
				return item.isCash ? '现金' : '现金等价物';
			},
			formatActivity(type) {
				if (type === 'OPERATING') return '经营活动';
				if (type === 'INVESTING') return '投资活动';
				if (type === 'FINANCING') return '筹资活动';
				return type || '-';
			},
			activityColor(type) {
				if (type === 'OPERATING') return 'blue';
				if (type === 'INVESTING') return 'cyan';
				if (type === 'FINANCING') return 'orange';
				return 'gray';
			},
			formatDirection(dir) {
				if (dir === 'INFLOW') return '流入';
				if (dir === 'OUTFLOW') return '流出';
				if (dir === 'NET') return '净额';
				if (dir === 'NONE') return '不计入';
				return dir || '-';
			},
			directionColor(dir) {
				if (dir === 'INFLOW') return 'green';
				if (dir === 'OUTFLOW') return 'red';
				if (dir === 'NET') return 'blue';
				return 'gray';
			},
			formatAccessRule(rule) {
				const code = Number(rule);
				switch (code) {
				case 0:
					return '净发生额(借-贷)';
				case 1:
					return '借方发生额';
				case 2:
					return '贷方发生额';
				case 3:
					return '余额';
				case 4:
					return '期末余额';
				case 6:
					return '期初余额';
				case 7:
					return '借方发生额(现金对方)';
				case 8:
					return '贷方发生额(现金对方)';
				case 9:
					return '净发生额(现金对方)';
				default:
					return `规则${rule}`;
				}
			},
			async applyStandardTemplate(override) {
				if (!this.currentAccountSets || !this.currentAccountSets.id) {
					this.$Message.error('未获取到账套信息');
					return;
				}
				if (override) {
					const confirmed = await this.$Confirm('确认覆盖现有标准模板配置？\n\n此操作将：\n• 清空当前现金等价物定义\n• 清空当前科目映射规则\n• 重新导入标准模板', '覆盖标准模板', {type: 'warning'})
						.then(() => true)
						.catch(() => false);
					if (!confirmed) return;
				}
				this.standardLoading = true;
				try {
					const {data} = await this.$api.report.cashFlowApplyStandard({override});
					this.$Message.success(`已应用标准模板：现金等价物${data.cashEquivalentsInserted || 0}条，映射${data.mappingsInserted || 0}条`);
					await this.loadPreset();
					this.fetchValidation();
				} catch (e) {
					this.$Message.error('应用失败：' + (e.message || '请稍后再试'));
				} finally {
					this.standardLoading = false;
				}
			},
			async applyConfig() {
				if (!this.currentAccountSets || !this.currentAccountSets.id) {
					this.$Message.error('未获取到账套信息，无法应用配置');
					return;
				}
				this.applyLoading = true;
				try {
					// 只保存科目映射配置，不保存formulas（由JSON模板控制）
					const payload = {
						accountSetsId: this.currentAccountSets.id,
						config: {
							cashEquivalents: this.config.cashEquivalents,
							flowCategories: this.config.flowCategories,
							subjectMappings: this.config.subjectMappings,
							formulas: [],  // 不保存formulas
							formulaTitles: {}
						}
					};
					await this.$api.report.cashFlowApply(payload);
					this.$Message.success('科目映射配置已保存');
				} catch (e) {
					this.$Message.error('保存失败：' + (e.message || '请稍后再试'));
				} finally {
					this.applyLoading = false;
				}
			},
			async fetchValidation() {
				if (!this.currentAccountSets || !this.currentAccountSets.id) {
					return;
				}
				if (!this.unmappedMonth) {
					this.unmappedMonth = this.currentMonth();
				}
				this.validateLoading = true;
				try {
					const {data} = await this.$api.report.cashFlowValidate({month: this.unmappedMonth, limit: 50});
					this.validation = data || {unmappedTotal: 0, mappingConflicts: [], unmappedWithActivity: []};
				} catch (e) {
					this.$Message.error('检查失败：' + (e.message || '请稍后再试'));
				} finally {
					this.validateLoading = false;
				}
			},
			async autoMapByStandard() {
				if (!this.currentAccountSets || !this.currentAccountSets.id) {
					return;
				}
				if (!this.unmappedMonth) {
					this.unmappedMonth = this.currentMonth();
				}
				this.autoMapLoading = true;
				try {
					const {data} = await this.$api.report.cashFlowAutoMap({month: this.unmappedMonth, limit: 200});
					this.$Message.success(`已自动补全映射 ${data.inserted || 0} 条`);
					this.fetchValidation();
				} catch (e) {
					this.$Message.error('自动补全失败：' + (e.message || '请稍后再试'));
				} finally {
					this.autoMapLoading = false;
				}
			},
			currentMonth() {
				const now = new Date();
				const year = now.getFullYear();
				const month = String(now.getMonth() + 1).padStart(2, '0');
				return `${year}-${month}`;
			},
			async resetToStandard() {
				if (!this.currentAccountSets || !this.currentAccountSets.id) {
					this.$Message.error('未获取到账套信息');
					return;
				}
				this.$Confirm('确认要将现金流量表重置为标准配置吗？\n\n此操作将：\n• 恢复标准的现金流量表公式\n• 使用改进的间接法计算逻辑\n• 清除当前的自定义公式\n\n重置后建议重新生成报表以查看效果。', '重置为标准配置', {
					confirmText: '确认重置',
					type: 'warning'
				}).then(async () => {
					this.resetLoading = true;
					try {
						// 查找现金流量表模板ID
						const templates = await this.$api.report.getTemplates(this.currentAccountSets.id);
						const cashFlowTemplate = templates.data.find(t => t.templateKey === 'cash-flow-statement');
						if (!cashFlowTemplate) {
							throw new Error('未找到现金流量表模板');
						}
						// 调用重置接口
						await this.$api.report.resetTemplate(this.currentAccountSets.id, cashFlowTemplate.id);
						this.$Message.success('现金流量表已重置为标准配置，建议重新生成报表');
						// 重新加载配置
						await this.loadPreset();
					} catch (e) {
						this.$Message.error('重置失败：' + (e.message || '请稍后再试'));
					} finally {
						this.resetLoading = false;
					}
				}).catch(() => {
					// 用户取消
				});
			},
			openAddMapping() {
				this.mappingEditMode = false;
				this.mappingForm = {
					subjectCode: '',
					subjectName: '',
					cashFlowCategoryCode: '',
					debitDirection: '',
					creditDirection: '',
					isActive: true,
					_id: null
				};
				this.mappingModalVisible = true;
			},
			openEditMapping(item) {
				this.mappingEditMode = true;
				this.mappingForm = {...item};
				this.mappingModalVisible = true;
				this.subjectDropdownVisible = false;
				this.handleSubjectInput(this.mappingForm.subjectCode);
				this.fetchSubjectNameIfEmpty(this.mappingForm.subjectCode);
			},
			async loadPreset() {
				const presets = cashFlowConfig.presets || {};
				const preset = presets[this.selectedPresetKey] || presets.enterprise || {};
				this.config = JSON.parse(JSON.stringify(preset));
				if (!this.unmappedMonth) {
					this.unmappedMonth = this.currentMonth();
				}
				if (!this.currentAccountSets || !this.currentAccountSets.id) {
					return;
				}
				try {
					const {data} = await this.$api.report.cashFlowConfig();
					this.mergeRemoteConfig(data || {});
				} catch (e) {
					this.$Message.error('加载当前账套配置失败：' + (e.message || '请稍后再试'));
				}
			},
			mergeRemoteConfig(remote) {
				if (remote.cashEquivalents && remote.cashEquivalents.length) {
					this.$set(this.config, 'cashEquivalents', remote.cashEquivalents);
				}
				if (remote.flowCategories && remote.flowCategories.length) {
					this.$set(this.config, 'flowCategories', remote.flowCategories);
				}
				if (remote.subjectMappings && remote.subjectMappings.length) {
					this.$set(this.config, 'subjectMappings', remote.subjectMappings.map((item, index) => ({
						...item,
						_id: `${item.subjectCode || 'mapping'}-${item.cashFlowCategoryCode || index}-${index}`
					})));
				}
				if (remote.formulas && remote.formulas.length) {
					this.$set(this.config, 'formulas', remote.formulas);
				}
			},
			saveMapping() {
				this.$refs.mappingFormRef.validate().then(() => {
					this.savingMapping = true;
					const list = this.config.subjectMappings || [];
					if (this.mappingEditMode) {
						const idx = list.findIndex(i => i._id === this.mappingForm._id);
						if (idx > -1) {
							this.$set(list, idx, {...this.mappingForm});
						}
					} else {
						this.mappingForm._id = Date.now();
						list.push({...this.mappingForm});
						this.$set(this.config, 'subjectMappings', list);
					}
					this.savingMapping = false;
					this.mappingModalVisible = false;
					this.$Message.success('已保存映射，记得一键应用同步后端');
				}).catch(() => {});
			},
			removeMapping(item) {
				this.$Confirm('确认删除该科目映射？', '提示').then(() => {
					this.config.subjectMappings = (this.config.subjectMappings || []).filter(i => i !== item);
					this.$Message.success('已删除');
				});
			},
			handleSubjectInput(val) {
				this.mappingForm.subjectCode = val;
				this.mappingForm.subjectName = '';
				this.debouncedSubjectSearch(val);
			},
			debouncedSubjectSearch(keyword) {
				clearTimeout(this.subjectSearchTimer);
				if (!keyword || keyword.trim().length < 2) {
					this.subjectOptions = [];
					this.subjectDropdownVisible = false;
					return;
				}
				this.subjectSearchTimer = setTimeout(() => {
					this.subjectLoading = true;
					this.$api.setting.subject.voucherSelect({keyword}).then(({data}) => {
						const options = (data || []).map(item => ({
							code: item.code || item.subjectCode,
							name: item.name || item.subjectName || ''
						}));
						this.applySubjectOptions(options);
					}).catch(() => {
						this.applySubjectOptions([]);
					}).finally(() => {
						this.subjectLoading = false;
					});
				}, 200);
			},
			selectSubject(option) {
				this.mappingForm.subjectCode = option.code;
				this.mappingForm.subjectName = option.name;
				this.subjectDropdownVisible = false;
				this.fetchSubjectNameIfEmpty(option.code);
			},
			applySubjectOptions(options) {
				this.subjectOptions = options;
				const exact = this.subjectOptions.find(opt => opt.code === this.mappingForm.subjectCode);
				if (exact) {
					this.mappingForm.subjectName = exact.name;
				} else if (!this.mappingForm.subjectName) {
					this.fetchSubjectNameIfEmpty(this.mappingForm.subjectCode);
				}
				this.subjectDropdownVisible = this.subjectOptions.length > 0;
			},
			flattenSubjects(list) {
				const res = [];
				const walk = (arr) => {
					(arr || []).forEach(item => {
						res.push({code: item.code, name: item.name});
						if (item.children && item.children.length) {
							walk(item.children);
						}
					});
				};
				walk(list);
				return res;
			},
			fetchSubjectNameIfEmpty(code) {
				if (!code || this.mappingForm.subjectName) return;
				if (String(code).length < 2) return;
				this.$api.setting.subject.voucherSelect({keyword: code}).then(({data}) => {
					const hit = (data || []).find(item => (item.code || item.subjectCode) === code);
					if (hit) {
						this.mappingForm.subjectName = hit.name || hit.subjectName || '';
					}
				});
			}
		},
		watch: {
			currentAccountSets: {
				immediate: true,
				handler() {
					this.loadPreset();
					this.fetchValidation();
				}
			}
		},
		mounted() {}
	};
</script>

<style scoped>
.cash-flow-config .actions {
	display: flex;
	gap: 8px;
}

.cash-flow-config .action-btn {
	min-width: 120px;
	font-weight: 600;
	border-radius: 6px;
	box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
	color: #111;
}

.cash-flow-config .action-btn.primary {
	background: #e5f0ff;
	border-color: #2f7cf6;
	color: #111;
}

.cash-flow-config .action-btn.success {
	background: #c8f5e5;
	border-color: #12c48b;
	color: #111;
}

.cash-flow-config .action-btn.warn {
	background: #ffe8cc;
	border-color: #f59e0b;
	color: #111;
}

.cash-flow-config .action-btn:not(.primary):not(.success):not(.warn) {
	color: #111;
	border-color: #cfd9e8;
}

.summary-area {
	background: #f8fbff;
	border-bottom: 1px solid #f0f2f5;
}

.summary-row .summary-card {
	background: #fff;
	border: 1px solid #f0f2f5;
	border-radius: 6px;
	padding: 12px;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
	height: 100%;
}

.summary-title {
	color: #4a5568;
	font-size: 13px;
	margin-bottom: 4px;
}

.summary-value {
	font-size: 20px;
	font-weight: 600;
	color: #1a73e8;
}

.summary-desc {
	color: #748198;
	font-size: 12px;
	margin-top: 2px;
}

.mode-notice {
	display: none;
}

.tab-section {
	background: #fff;
	border-top: 1px solid #f0f2f5;
}

.toolbar {
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 12px;
	flex-wrap: wrap;
}

.unmapped-panel {
	background: #fff8f1;
	border: 1px solid #fde2c4;
	border-radius: 6px;
	padding: 10px;
	margin-bottom: 12px;
}

.unmapped-panel .panel-title {
	font-weight: 600;
	color: #9a5b13;
	margin-bottom: 8px;
}

.mapping-title {
	font-weight: 500;
	color: #1f2d3d;
}

.mapping-meta {
	color: #94a3b8;
	font-size: 12px;
}

.subject-suggest {
	position: absolute;
	left: 0;
	right: 0;
	max-height: 180px;
	overflow: auto;
	border: 1px solid #e5e7eb;
	border-radius: 4px;
	background: #fff;
	box-shadow: 0 4px 10px rgba(0,0,0,0.08);
	margin-top: 4px;
	z-index: 10000;
}

.subject-input-wrapper {
	position: relative;
}

.manual-input {
	width: 100%;
	max-width: 500px;
	border: 1px solid #d9d9d9;
	border-radius: 4px;
	padding: 8px 10px;
	outline: none;
}

.manual-input:focus {
	border-color: #2f7cf6;
	box-shadow: 0 0 0 2px rgba(47, 124, 246, 0.15);
}

.subject-suggest .item {
	padding: 8px 10px;
	cursor: pointer;
	display: flex;
	justify-content: space-between;
}

.subject-suggest .item:hover {
	background: #f5f8ff;
}

.subject-suggest .code {
	font-weight: 600;
	color: #1f2d3d;
}

.subject-suggest .name {
	color: #52606d;
	margin-left: 12px;
	flex: 1;
	text-align: right;
}
</style>
